import { useCallback, useState } from 'react';
import { type EntitlementsView, getEntitlements, setEntitlement } from '../lib/internal.js';
import { useAsyncData } from '../lib/useAsyncData.js';

/**
 * Per-account feature entitlements. Pricing is case-by-case, so an operator flips
 * each feature on/off per account here. `workspaces` is backed by its own column;
 * the rest live in the entitlements JSON. Each feature is badged **live** (built &
 * available) or **roadmap** (not built yet — toggling pre-grants it for when it ships).
 */
export function EntitlementsPanel({ accountId }: { accountId: string }) {
  const { data, error, reload } = useAsyncData(
    useCallback(() => getEntitlements(accountId), [accountId]),
  );
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (feature: string, enabled: boolean) => {
    setBusy(feature);
    try {
      await setEntitlement(accountId, feature, enabled);
      await reload();
    } finally {
      setBusy(null);
    }
  };

  if (error) return <span className="text-xs text-rose-600">Couldn’t load entitlements</span>;

  return (
    <div className="rounded-lg border border-neutral-200/70 bg-white p-3">
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-neutral-500">
        Feature entitlements
      </div>
      {!data ? (
        <span className="text-xs text-neutral-400">Loading…</span>
      ) : (
        <ul className="divide-y divide-neutral-100">
          {Object.entries((data as EntitlementsView).features).map(([key, def]) => {
            const on = data.entitlements[key] ?? false;
            return (
              <li key={key} className="flex items-center justify-between py-1.5">
                <span className="text-sm">
                  {def.label}
                  {def.roadmap ? (
                    <span
                      className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700"
                      title="Not built yet — toggling pre-grants it for when it ships."
                    >
                      roadmap
                    </span>
                  ) : (
                    <span
                      className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700"
                      title="Built and available — toggle to turn it on for this account."
                    >
                      live
                    </span>
                  )}
                </span>
                <button
                  type="button"
                  disabled={busy === key}
                  onClick={() => toggle(key, !on)}
                  className={`relative h-5 w-9 rounded-full transition disabled:opacity-50 ${on ? 'bg-emerald-500' : 'bg-neutral-300'}`}
                  aria-pressed={on}
                  aria-label={`${on ? 'Disable' : 'Enable'} ${def.label}`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${on ? 'left-4' : 'left-0.5'}`}
                  />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
